import { Stack, Box, Card, CardActions, CardContent, CardHeader, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactElement, useState } from 'react';
import {
  ChipsType,
  timeSince,
  WsButton,
  WsChip,
  WsChipVariants,
  WsColors,
  WsIconText,
  WsIconViewJD,
  WsTagsList,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsIconTag,
  highlightKeys,
} from 'wisestep-base';

import PulseIconText from '../../../components/PulseIconText';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';

import CandidateAvatar from './CandidateAvatar';

interface CandidateCardProps {
  candidateId: string;
  title: string;
  imgSrc?: string;
  isDownloaded: boolean;
  showRelevance?: boolean;
  relevance?: string;
  isViewCv?: boolean;
  name: string;
  skills: string[];
  profileDescription?: string;
  detailItems: Array<{
    icon: ReactElement;
    label: string;
    tooltip?: string;
  }>;
  onViewCvClick: (candidateId: string) => Promise<void>;
  onViewDetailsClick?: (candidateId: string) => Promise<void>;
  dateLastActive?: string;
  dateResumeLastUpdated?: string;
  viewCvLabel?: string;
  dateCreated?: string;
  IsAnonymous?: boolean;
  skillsDisplayCount?: number;
}

const CandidateCard = ({
  candidateId,
  title,
  imgSrc = '',
  isDownloaded = false,
  showRelevance = false,
  relevance = '',
  isViewCv = true,
  name,
  skills,
  detailItems,
  profileDescription = '',
  onViewCvClick,
  onViewDetailsClick = async () => {
    await new Promise<void>((resolve) => resolve());
  },
  dateLastActive,
  viewCvLabel = 'View CV',
  dateResumeLastUpdated,
  dateCreated,
  IsAnonymous = false,
  skillsDisplayCount = 24,
}: CandidateCardProps) => {
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const setUpdateJBViews = useConversationWidgetStore((state) => state.setUpdateJBViews);
  const [isResumeDownloaded, setIsDownloaded] = useState<boolean>(isDownloaded);
  const [handleViewCvClick, setHandleViewCvClick] = useState(false);

  const onViewCV = async () => {
    setHandleViewCvClick(true);
    await onViewCvClick(candidateId);
    if (!isDownloaded && !isResumeDownloaded) {
      setUpdateJBViews(true);
    }
    setIsDownloaded(true);
    setHandleViewCvClick(false);
  };
  return (
    <Card>
      <CardHeader
        avatar={<CandidateAvatar name={name} src={imgSrc} />}
        sx={{ paddingBottom: 0 }}
        title={
          <Grid container display="flex" justifyContent="space-between">
            <Grid item xs={6}>
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK}>
                {title}
              </WsTypography>
            </Grid>
            <Grid item xs={6} display="flex" justifyContent="flex-end">
              <Stack direction="row" alignItems={'center'}>
                {showRelevance ? (
                  <WsChip value={relevance} variant={WsChipVariants.PRIMARY} />
                ) : (
                  <></>
                )}
                {isResumeDownloaded ? (
                  <WsChip value="Downloaded" variant={WsChipVariants.PRIMARY} />
                ) : (
                  <></>
                )}
              </Stack>
            </Grid>
          </Grid>
        }
      />
      <CardContent>
        <Grid container spacing={1} px={2}>
          {detailItems.map((detailItem) => {
            if (detailItem.label) {
              return (
                <Grid item xs={3} key={detailItem.label}>
                  <WsIconText
                    icon={detailItem.icon ?? <></>}
                    text={
                      searchQuery
                        ? highlightKeys(String(detailItem.label), searchQuery ?? '')
                        : String(detailItem.label)
                    }
                    opacity={true}
                    popOverText={`${detailItem.tooltip}`}
                    textCursor="pointer"
                    isHtml={!isEmpty(searchQuery)}
                  />
                </Grid>
              );
            }
          })}
          {profileDescription ? (
            <Grid item xs={12}>
              <div
                className="content"
                dangerouslySetInnerHTML={{ __html: profileDescription }}
              ></div>
            </Grid>
          ) : (
            ''
          )}
        </Grid>
        {!isEmpty(skills) && (
          <Grid container item xs={12} pt={2} alignItems="center" pl={1}>
            <Box pt={0.5} pr={0.6}>
              <WsIconTag />
            </Box>
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
            >
              Skills :
            </WsTypography>
            <WsTagsList
              chipArray={skills}
              count={skillsDisplayCount}
              type={ChipsType.PRIMARY}
              borderColorFlag={true}
              query={searchQuery ?? ''}
            />
          </Grid>
        )}
      </CardContent>
      <CardActions disableSpacing sx={{ padding: 0 }}>
        <Grid
          sx={{ backgroundColor: WsColors.TheBlues25 }}
          px={1.5}
          container
          justifyContent="space-between"
        >
          <Grid item p={1} justifyContent="center">
            {dateCreated && (
              <PulseIconText
                text={`Profile created ${timeSince(dateCreated)}`}
                hover={'Profile created On: ' + dateCreated}
              />
            )}
            {dateLastActive && (
              <PulseIconText
                text={`Last Active ${timeSince(dateLastActive)}`}
                hover={'Last Active On: ' + dateLastActive}
              />
            )}
            {dateResumeLastUpdated && (
              <PulseIconText
                text={`Resume Updated ${timeSince(dateResumeLastUpdated)}`}
                hover={'Resume Updated On: ' + dateResumeLastUpdated}
              />
            )}
          </Grid>
          <Grid item py={1}>
            {!IsAnonymous && (
              <>
                {isViewCv ? (
                  <WsButton
                    onClick={() => onViewCV()}
                    variant="text"
                    size="large"
                    startIcon={<WsIconViewJD />}
                    disabled={handleViewCvClick}
                  >
                    {viewCvLabel}
                  </WsButton>
                ) : (
                  <WsButton
                    onClick={() => onViewDetailsClick(candidateId)}
                    variant="text"
                    size="large"
                    startIcon={<WsIconViewJD />}
                  >
                    View details
                  </WsButton>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </CardActions>
    </Card>
  );
};

CandidateCard.displayName = 'CandidateCard';
export default CandidateCard;
